import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { BasicInfoSection } from '../components/basic-info/BasicInfoSection';
import { SocialSection } from '../components/social/SocialSection';
import { PersonalitySection } from '../components/personality/PersonalitySection';
import { PersonalityConfigSection } from '../components/personality-config/PersonalityConfigSection';
import { TrainingSection } from '../components/training/TrainingSection';
import { ProfileImageSection } from '../components/profile-image/ProfileImageSection';

import { useCreateEchoDraft } from '../hooks/useCreateEchoDraft';
import { useToast } from '@/context/ToastContext';
import { useEchoStore } from '@/context/EchoContext';
import { echoService } from '@/services/echoService';
import { convertToWebp } from '@/utils/image';
import waveContactListSvg from '@/assets/images/svg/wave_element_contact_list.svg';
import clsx from 'clsx';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import type {
  CreateEchoDraftRequest,
  EchoDraftResponse,
  PersonalityConfig,
} from '@/types/echo_creation';

type CreateEchoStep = 'basic-info' | 'profile-image' | 'setup' | 'training' | 'personality-config';

const PERSONALITY_COMPLETE_STATUSES = new Set([
  'personality_test_done',
  'training_uploaded',
  'training_in_progress',
  'training_completed',
  'published',
  'pending',
  'approved',
]);

const CONFIG_READY_STATUSES = new Set(['training_completed', 'pending', 'approved']);
const TRAINING_STATUSES = new Set([
  'personality_test_done',
  'training_uploaded',
  'training_in_progress',
]);

const VALID_STEPS = new Set<CreateEchoStep>([
  'basic-info',
  'profile-image',
  'setup',
  'training',
  'personality-config',
]);

function resolveInitialStep(
  draft: EchoDraftResponse | null | undefined,
  draftStatus: string,
  isRetrain: boolean,
  retrainStep: string | null
): CreateEchoStep {
  // Retrain: resume from URL-persisted step, or start at basic-info
  if (isRetrain) {
    if (retrainStep && VALID_STEPS.has(retrainStep as CreateEchoStep)) {
      return retrainStep as CreateEchoStep;
    }
    return 'basic-info';
  }

  const isBasicInfoComplete =
    !!draft?.name && !!draft?.domain && !!draft?.category && !!draft?.bio && draft.bio.length >= 10;
  if (!isBasicInfoComplete) return 'basic-info';

  const isPersonalityComplete = PERSONALITY_COMPLETE_STATUSES.has(draftStatus);
  if (!draft?.profile_image_url && !isPersonalityComplete) return 'profile-image';

  if (!isPersonalityComplete) return 'setup';
  if (draft?.persona_json && draftStatus === 'training_uploaded') {
    return 'personality-config';
  }
  if (TRAINING_STATUSES.has(draftStatus)) return 'training';
  if (CONFIG_READY_STATUSES.has(draftStatus) || draftStatus === 'published') {
    return 'personality-config';
  }
  return 'setup';
}

/** Multi-step wizard for creating a Digital Echo. Resumes progress from draft status. */
export const CreateEchoPage: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const {
    draftId,
    draftStatus,
    draft,
    isLoadingDraft,
    isCreating,
    createDraft,
    refreshDraft,
    searchParams,
    setSearchParams,
  } = useCreateEchoDraft();

  const isRetrain = searchParams.get('retrain') === 'true';
  const retrainStepFromUrl = searchParams.get('step');
  const { refreshEchoes } = useEchoStore();

  const [step, setStepRaw] = React.useState<CreateEchoStep>('basic-info');

  const setSearchParamsRef = React.useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const setStep = React.useCallback(
    (nextStep: CreateEchoStep) => {
      setStepRaw(nextStep);
      if (isRetrain) {
        setSearchParamsRef.current(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('step', nextStep);
            return next;
          },
          { replace: true }
        );
      }
    },
    [isRetrain]
  );
  const [greetings, setGreetings] = React.useState<string[]>(['']);
  const [isPublicFigure, setIsPublicFigure] = React.useState(false);
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = React.useState(false);
  const [preloadedPersonalityConfig, setPreloadedPersonalityConfig] = React.useState<
    PersonalityConfig | undefined
  >();
  const pendingAvatarRef = React.useRef<File | null>(null);
  const avatarBlobUrlRef = React.useRef<string | null>(null);
  const avatarUploadAbortRef = React.useRef<AbortController | null>(null);

  const hasRestored = React.useRef(false);

  const basicInfoInitialData = React.useMemo(
    () =>
      draft
        ? {
            name: draft.name || '',
            domain: draft.domain || '',
            category: draft.category || '',
            bio: draft.bio || '',
          }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute when field values change, not the whole draft object
    [draft?.name, draft?.domain, draft?.category, draft?.bio]
  );

  React.useEffect(() => {
    if (!isLoadingDraft && draftStatus && !hasRestored.current) {
      setStep(resolveInitialStep(draft, draftStatus, isRetrain, retrainStepFromUrl));

      if (draft?.greetings?.length) setGreetings(draft.greetings);
      if (draft?.is_public_figure) setIsPublicFigure(draft.is_public_figure);
      if (draft?.profile_image_url) setAvatarUrl(draft.profile_image_url);

      hasRestored.current = true;
    }
  }, [draftStatus, isLoadingDraft, draft, isRetrain, retrainStepFromUrl, setStep]);

  React.useEffect(
    () => () => {
      if (avatarBlobUrlRef.current) {
        URL.revokeObjectURL(avatarBlobUrlRef.current);
      }
      avatarUploadAbortRef.current?.abort();
    },
    []
  );

  React.useEffect(() => {
    if (draftId && pendingAvatarRef.current) {
      uploadAvatarToDraft(draftId, pendingAvatarRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  const uploadAvatarToDraft = async (targetDraftId: string, file: File) => {
    avatarUploadAbortRef.current?.abort();
    const controller = new AbortController();
    avatarUploadAbortRef.current = controller;

    setIsUploadingAvatar(true);
    try {
      const webpFile = await convertToWebp(file);
      const result = await echoService.uploadDraftAvatar(targetDraftId, webpFile);

      if (controller.signal.aborted) return;

      if (avatarBlobUrlRef.current) {
        URL.revokeObjectURL(avatarBlobUrlRef.current);
        avatarBlobUrlRef.current = null;
      }
      setAvatarUrl(result.url);
      pendingAvatarRef.current = null;
    } catch (err) {
      if (controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
        return;
      }

      if (avatarBlobUrlRef.current) {
        URL.revokeObjectURL(avatarBlobUrlRef.current);
        avatarBlobUrlRef.current = null;
      }
      addToast({
        title: 'Upload Failed',
        subtitle: 'Could not upload avatar. Please try again.',
        type: 'error',
      });
      setAvatarUrl(draft?.profile_image_url ?? null);
      pendingAvatarRef.current = null;
    } finally {
      if (!controller.signal.aborted) {
        setIsUploadingAvatar(false);
      }
    }
  };

  const handleAvatarSelect = (file: File | null) => {
    if (!file) {
      if (avatarBlobUrlRef.current) {
        URL.revokeObjectURL(avatarBlobUrlRef.current);
        avatarBlobUrlRef.current = null;
      }
      setAvatarUrl(null);
      pendingAvatarRef.current = null;
      return;
    }

    if (avatarBlobUrlRef.current) {
      URL.revokeObjectURL(avatarBlobUrlRef.current);
    }

    const previewUrl = URL.createObjectURL(file);
    avatarBlobUrlRef.current = previewUrl;
    setAvatarUrl(previewUrl);

    if (draftId) {
      uploadAvatarToDraft(draftId, file);
    } else {
      pendingAvatarRef.current = file;
    }
  };

  const handleBasicInfoSubmit = async (data: CreateEchoDraftRequest) => {
    hasRestored.current = true;
    const cleanedGreetings = greetings.filter((greeting) => greeting.trim());
    await createDraft({ ...data, greetings: cleanedGreetings, is_public_figure: isPublicFigure });
    refreshEchoes();
    setStep('profile-image');
  };

  const handleBasicInfoSaveForLater = async (data: Partial<CreateEchoDraftRequest>) => {
    hasRestored.current = true;
    const cleanedGreetings = greetings.filter((greeting) => greeting.trim());
    await createDraft({
      ...data,
      greetings: cleanedGreetings,
      is_public_figure: isPublicFigure,
    } as CreateEchoDraftRequest);
    refreshEchoes();
    setStep('profile-image');
    addToast({ title: 'Progress Saved', subtitle: 'Proceeding to next step...', type: 'success' });
  };

  const handleProfileImageContinue = () => {
    setStep('setup');
  };

  const handleProfileImageBack = () => {
    setStep('basic-info');
  };

  const handlePersonalityQuizComplete = () => {
    // Refresh draft so personality_test_results are available for hydration on back-nav
    refreshDraft();
    setStep('training');
  };

  const handleTrainingBack = () => {
    setStep('setup');
  };

  const handleTrainingComplete = (preloadedConfig?: PersonalityConfig) => {
    refreshEchoes();
    setPreloadedPersonalityConfig(preloadedConfig);
    setStep('personality-config');
  };

  const handleFinalConfigComplete = () => {
    refreshEchoes();
    navigate('/my-echo');
  };

  const handleSetupSaveForLater = () => {
    refreshEchoes();
    setStep('training');
    addToast({ title: 'Progress Saved', subtitle: 'Proceeding to training...', type: 'success' });
  };

  const handleTrainingSaveForLater = () => {
    refreshEchoes();
    setStep('personality-config');
    addToast({
      title: 'Progress Saved',
      subtitle: 'Proceeding to configuration...',
      type: 'success',
    });
  };

  if (isLoadingDraft) {
    return (
      <div className='flex size-full items-center justify-center bg-white'>
        <div className='text-center'>
          <div className='size-8 animate-spin rounded-full border-4 border-brand-300 border-t-brand-800' />
          <p className='mt-4 font-sans text-brand-800'>Resuming your echo...</p>
        </div>
      </div>
    );
  }

  const showIdentityHeader = step === 'basic-info';

  return (
    <div className='relative size-full overflow-hidden bg-white'>
      <div className='pointer-events-none absolute inset-0 bg-account-page-gradient' />
      <img
        src={waveContactListSvg}
        alt=''
        className='pointer-events-none absolute bottom-0 left-0 z-0 select-none opacity-50'
      />

      <div className='custom-scrollbar relative z-10 size-full overflow-y-auto p-6 md:p-10'>
        <div className='w-full'>
          {showIdentityHeader && (
            <>
              <PageHeader
                title='IDENTITY'
                titleClassName='!text-left font-proxima font-extrabold text-24 leading-47 text-brand-800'
                theme='dark'
              />
              <div className='-mt-4 mb-4 font-sans text-16 font-medium leading-27 text-gray-530'>
                Let&apos;s start by defining who your digital twin is. This helps fans understand
                what to expect when they interact with your Echo.
              </div>
            </>
          )}

          <div
            className={clsx(
              'animate-slide-in flex flex-col',
              step === 'training' || step === 'personality-config'
                ? 'max-w-[1100px] wide:max-w-full'
                : step === 'profile-image'
                  ? 'max-w-[900px] wide:max-w-full'
                  : 'max-w-4xl wide:max-w-full'
            )}
          >
            {/* STEP 1: BASIC INFO */}
            {step === 'basic-info' && (
              <BasicInfoSection
                onCreate={handleBasicInfoSubmit}
                onSaveAndExit={handleBasicInfoSaveForLater}
                isSubmitting={isCreating}
                draftId={draftId ?? null}
                greetings={greetings}
                onGreetingsChange={setGreetings}
                isPublicFigure={isPublicFigure}
                onPublicFigureChange={setIsPublicFigure}
                {...(basicInfoInitialData ? { initialData: basicInfoInitialData } : {})}
              />
            )}

            {/* STEP 1.5: PROFILE IMAGE */}
            {step === 'profile-image' && (
              <ProfileImageSection
                profileImageUrl={avatarUrl}
                onAvatarSelect={handleAvatarSelect}
                isUploadingAvatar={isUploadingAvatar}
                onContinue={handleProfileImageContinue}
                onBack={handleProfileImageBack}
              />
            )}

            {/* STEP 2: SETUP (Social + Personality Quiz Combined) */}
            {step === 'setup' && draftId && (
              <>
                <div className='mb-2 flex items-center gap-3'>
                  <button
                    type='button'
                    onClick={() => setStep('profile-image')}
                    className='rounded-full p-1 transition-colors hover:bg-gray-100'
                    aria-label='Go back'
                  >
                    <ArrowLeftIcon className='size-6 text-dark-100' strokeWidth={2.5} />
                  </button>
                  <h2 className='font-proxima text-24 font-extrabold uppercase leading-47 text-brand-800 md:text-40'>
                    CREATE ECHO
                  </h2>
                </div>
                <SocialSection draftId={draftId} draft={draft ?? null} />
                <div className='mt-12'>
                  <PersonalitySection
                    draftId={draftId}
                    draft={draft ?? null}
                    onComplete={handlePersonalityQuizComplete}
                    onSaveForLater={handleSetupSaveForLater}
                  />
                </div>
              </>
            )}

            {/* STEP 3: TRAINING & PUBLISH */}
            {step === 'training' && draftId && (
              <TrainingSection
                draftId={draftId}
                {...(draftStatus ? { draftStatus } : {})}
                isRetrainMode={isRetrain}
                onBack={handleTrainingBack}
                onTrainingComplete={handleTrainingComplete}
                onSaveForLater={handleTrainingSaveForLater}
              />
            )}

            {/* STEP 4: CONFIGURATION (Results/Fine-tune) */}
            {step === 'personality-config' && draftId && (
              <PersonalityConfigSection
                draftId={draftId}
                onBack={() => setStep('training')}
                onComplete={handleFinalConfigComplete}
                onSaveForLater={() => navigate('/my-echo')}
                {...(preloadedPersonalityConfig
                  ? { initialConfig: preloadedPersonalityConfig }
                  : {})}
              />
            )}
          </div>
        </div>

        <div className='h-20' />
      </div>
    </div>
  );
};

export default CreateEchoPage;
